import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE = '/api';

/**
 * A practical next step, keyed off the gateway's Hebrew decline label. The
 * label itself (e.g. "סירוב חברת האשראי (קוד 004)") is shown verbatim above
 * it — donors and orgs asked to see the difference between "the terminal
 * doesn't take this card brand" and "wrong CVV" (צוות 7, Sept 2026).
 */
const hintFor = (reason: string): string => {
  const r = reason || '';
  if (/מותג|סוג הכרטיס|כרטיס ישראלי|כרטיס זר|לא נתמך/.test(r)) {
    return 'המסוף של העמותה אינו מקבל את סוג הכרטיס הזה. נסו כרטיס מסוג אחר, או שלמו בביט אם האפשרות מוצעת.';
  }
  if (/שגוי|תוקף|CVV|ספרות|מספר כרטיס|תעודת זהות/i.test(r)) {
    return 'בדקו את פרטי הכרטיס שהוזנו (מספר, תוקף, 3 ספרות בגב הכרטיס, ת״ז) ונסו שוב.';
  }
  if (/סירוב|חסום|התקשר|חברת האשראי|גנוב|מסגרת/.test(r)) {
    return 'חברת האשראי דחתה את החיוב. כדאי לפנות אליה לבירור, או לנסות כרטיס אחר.';
  }
  if (/תקשורת|תקלה|נסות שוב/.test(r)) {
    return 'תקלה זמנית מול חברת האשראי. המתינו רגע ונסו שוב.';
  }
  return '';
};

const PaymentFailedPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const campaignSlug = searchParams.get('campaign') || '';
  // `session` is what our own fail_url carries; `Order` is the SaaS billing
  // flow's spelling of the same PaymentSession id.
  const sessionId = searchParams.get('session') || searchParams.get('Order') || '';
  // Some redirect paths already put a Hebrew message in `error` (HyP);
  // machine tokens like "payment_failed" are not worth showing.
  const errorParam = searchParams.get('error') || '';
  const initialReason = /[֐-׿]/.test(errorParam) ? errorParam : '';
  const isInIframe = typeof window !== 'undefined' && window.parent !== window;
  const notifiedRef = useRef(false);
  const [reason, setReason] = useState<string>(initialReason);

  // The gateway's server-to-server notify (which stores the decline reason)
  // and the donor's redirect here race each other, so poll briefly.
  useEffect(() => {
    if (!sessionId || !/^[a-f0-9]{24}$/i.test(sessionId) || reason) return;
    let cancelled = false;
    let attempt = 0;
    const tick = async () => {
      attempt += 1;
      try {
        const res = await fetch(`${API_BASE}/payments/failure-reason?session=${encodeURIComponent(sessionId)}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data?.reason) { setReason(String(data.reason)); return; }
        }
      } catch { /* keep polling */ }
      if (!cancelled && attempt < 4) setTimeout(tick, 1500);
    };
    void tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (!isInIframe || notifiedRef.current) return;
    notifiedRef.current = true;
    const payload: Record<string, string> = {};
    searchParams.forEach((v, k) => { payload[k] = v; });
    if (reason) payload.reason = reason;
    try {
      window.parent.postMessage({ type: 'amutech-payment-failed', payload }, '*');
    } catch {}
  }, [isInIframe, searchParams, reason]);

  const hint = hintFor(reason);

  if (isInIframe) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="text-center max-w-sm">
          <div style={{ fontSize: 56, marginBottom: 12 }}>😔</div>
          <p className="text-red-600 font-semibold">התשלום לא הושלם</p>
          {reason && <p className="text-sm text-gray-800 font-medium mt-2">סיבת הדחייה: {reason}</p>}
          {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #fef2f2, #fff1f2)' }}>
      <div className="text-center w-full max-w-md py-10 px-4">
        <div style={{ fontSize: 72, marginBottom: 16 }}>😔</div>
        <h1 className="text-2xl md:text-4xl font-black text-red-600 mb-3">התשלום לא הושלם</h1>
        {reason ? (
          <div className="bg-white/80 border border-red-100 rounded-xl px-4 py-3 mb-4 text-right">
            <p className="text-sm text-gray-500 mb-1">סיבת הדחייה מחברת האשראי:</p>
            <p className="text-base md:text-lg text-gray-900 font-bold">{reason}</p>
            {hint && <p className="text-sm text-gray-600 mt-2 leading-relaxed">{hint}</p>}
          </div>
        ) : (
          <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-2">משהו השתבש בתהליך התשלום.</p>
        )}
        <p className="text-sm text-gray-400 mb-8">אפשר לנסות שוב או ליצור קשר לעזרה.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          {campaignSlug && (
            <a href={`/campaign/${campaignSlug}`}
              className="px-7 py-3.5 rounded-xl bg-red-600 text-white font-bold text-base no-underline hover:bg-red-700 transition-colors">
              ← נסה שוב
            </a>
          )}
          <a href="/"
            className="px-7 py-3.5 rounded-xl bg-white text-gray-700 font-semibold text-base no-underline border-2 border-gray-200 hover:bg-gray-50 transition-colors">
            לדף הבית
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailedPage;
